import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { MatchPublic, PredictionPublic } from '@mondy/shared';
import {
  canPredictEarly,
  isMatchLive,
  liveMinuteLabel,
  matchPausedLabel,
  predictionWindowState,
} from '@mondy/shared';
import { cn } from '@/lib/cn';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  STAGE_LABELS,
  formatKickoffDateShort,
  formatKickoffShort,
  isKickoffToday,
  matchLabel,
} from '@/lib/format';
import { TeamFlag } from '@/components/TeamFlag';
import { LockBadge } from '@/components/LockBadge';
import { useBracketProjection } from '@/hooks/useBracketProjection';
import { useFavoriteTeamTheme } from '@/hooks/useFavoriteTeamTheme';

function formatOpensIn(opensAtMs: number): string {
  const delta = opensAtMs - Date.now();
  if (delta <= 0) return 'soon';
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

interface Props {
  m: MatchPublic;
  prediction?: PredictionPublic;
  id?: string;
}

function ScoreBlock({ m }: { m: MatchPublic }) {
  const result = m.result;
  if (isMatchLive(m) && result) {
    const main = result.extraTime ?? result.fullTime;
    const pens = result.penalties;
    const paused = m.status === 'paused';
    return (
      <div className="mc-score">
        <div className="live-score tabular">
          {pens ? (
            <>
              <span className="pens-side">({pens.team1})</span>
              {main.team1}–{main.team2}
              <span className="pens-side">({pens.team2})</span>
            </>
          ) : (
            <>
              {main.team1}–{main.team2}
            </>
          )}
        </div>
        {paused ? (
          <div className="mc-status live paused">
            {matchPausedLabel(m.minute)}
          </div>
        ) : (
          <div className="mc-status live">
            <span className="live-dot" />
            LIVE
            {typeof m.minute === 'number' ? (
              <span className="live-minute">{liveMinuteLabel(m.minute, m.addedTime)}</span>
            ) : null}
          </div>
        )}
      </div>
    );
  }
  if (m.status === 'finished' && result) {
    const main = result.extraTime ?? result.fullTime;
    return (
      <div className="mc-score">
        <div className="tabular">
          {main.team1}–{main.team2}
        </div>
        <div className="ft">
          {result.penalties
            ? `Pens ${result.penalties.team1}–${result.penalties.team2}`
            : result.extraTime
              ? 'a.e.t.'
              : 'Full time'}
        </div>
      </div>
    );
  }
  const today = isKickoffToday(m.kickoffUtc);
  return (
    <div className="mc-score upcoming">
      <div className="kick">
        {today ? 'Kick-off' : formatKickoffDateShort(m.kickoffUtc)}
      </div>
      <div className="tabular">{formatKickoffShort(m.kickoffUtc)}</div>
    </div>
  );
}

function PredictionBlock({
  m,
  prediction,
}: {
  m: MatchPublic;
  prediction?: PredictionPublic;
}) {
  const user = useCurrentUser();
  const finished = m.status === 'finished' && m.result;
  const isLive = isMatchLive(m);

  if (m.status === 'scheduled') {
    const teamsResolved = m.team1 !== null && m.team2 !== null;
    const allowEarlyPrediction =
      !!user && canPredictEarly(user.paymentTier) && teamsResolved;
    const windowState = predictionWindowState(m, { allowEarlyPrediction });
    if (windowState.state === 'too_early') {
      return (
        <div className="mc-you">
          <div className="lbl">Opens in</div>
          <div className="pred pred-soon tabular">
            {formatOpensIn(windowState.opensAtMs)}
          </div>
          <div
            className="pts soon"
            aria-label="Predictions not open yet"
            title="Predictions open 24h before kickoff"
          >
            <LockIcon />
          </div>
        </div>
      );
    }
  }

  if (!prediction) {
    const scheduled = m.status === 'scheduled';
    if (scheduled) {
      return (
        <div className="mc-you pill-only">
          <div className="pts open">Predict →</div>
        </div>
      );
    }
    return (
      <div className="mc-you">
        <div className="lbl">Your pick</div>
        <div className="pred" style={{ color: 'var(--fg-dim)' }}>
          — –
        </div>
        <div className="pts missed">Missed</div>
      </div>
    );
  }

  const ft = prediction.prediction.fullTime;
  const et = prediction.prediction.extraTime;
  const pens = prediction.prediction.penalties;
  const tieBreaker = pens
    ? `Pens ${pens.team1}–${pens.team2}`
    : et
      ? `a.e.t. ${et.team1}–${et.team2}`
      : null;
  const pts = prediction.pointsAwarded;
  const ptsClass =
    finished && typeof pts === 'number'
      ? pts === 3
        ? 'w3'
        : pts >= 1
          ? 'w1'
          : 'w0'
      : '';

  // Live "pending exact" cue when the user's FT matches the current score.
  const liveFt = isLive && m.result ? m.result.fullTime : null;
  const isExactPending =
    liveFt && liveFt.team1 === ft.team1 && liveFt.team2 === ft.team2;

  let indicator: ReactNode = null;
  if (finished && typeof pts === 'number') {
    indicator = <div className={cn('pts', ptsClass)}>+{pts} pts</div>;
  } else if (isExactPending) {
    indicator = <div className="pts pending">+3 live</div>;
  } else if (isLive) {
    indicator = (
      <div className="pts-lock" aria-label="Prediction locked" title="Locked">
        <LockIcon />
      </div>
    );
  }

  return (
    <div className="mc-you">
      <div className="lbl">Your pick</div>
      <div className="pred tabular">
        {ft.team1}–{ft.team2}
      </div>
      {tieBreaker ? <div className="pred-sub">{tieBreaker}</div> : null}
      {indicator}
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function MatchCard({ m, prediction, id }: Props) {
  const { theme: favoriteTheme } = useFavoriteTeamTheme();
  const { t1, t2 } = matchLabel(m);
  // A knockout match without its real teams yet shows its PROJECTED entrants
  // (and a ✓/? certainty badge), resolved from live standings exactly as the
  // bracket does. Display-only — every result/prediction path below stays on m.*.
  const needsProjection = m.stage !== 'group' && !m.team1;
  const { resolved, lockedIds } = useBracketProjection(needsProjection);
  const proj = needsProjection ? resolved?.get(m.id) : undefined;
  const team1 = proj?.team1 ?? m.team1;
  const team2 = proj?.team2 ?? m.team2;
  const lock1 = needsProjection && lockedIds && team1 ? lockedIds.has(team1.id) : null;
  const lock2 = needsProjection && lockedIds && team2 ? lockedIds.has(team2.id) : null;
  const result = m.result;
  const finalScore = result ? (result.extraTime ?? result.fullTime) : null;
  const w1 =
    m.status === 'finished' && finalScore
      ? finalScore.team1 > finalScore.team2
      : false;
  const w2 =
    m.status === 'finished' && finalScore
      ? finalScore.team2 > finalScore.team1
      : false;
  const scored =
    m.status === 'finished' &&
    prediction &&
    typeof prediction.pointsAwarded === 'number';
  const favoriteCode = favoriteTheme?.code;
  const isFavoriteMatch =
    !!favoriteCode &&
    (m.team1?.fifaCode === favoriteCode || m.team2?.fifaCode === favoriteCode);

  return (
    <Link
      id={id}
      to={`/matches/${m.id}`}
      className={cn(
        'match-card',
        isFavoriteMatch && 'fav',
        isMatchLive(m) && 'live',
        scored && 'scored',
      )}
    >
      <div className="accent-edge" />
      <div className="mc-stage">
        <div className="n">{String(m.matchNumber).padStart(2, '0')}</div>
        <div className="s">
          {m.group ? `Grp ${m.group}` : STAGE_LABELS[m.stage]}
        </div>
      </div>
      <div className="mc-teams">
        <div className={cn('mc-team', !team1 && 'unresolved', w1 && 'winner', w2 && 'loser')}>
          <span className="ind" />
          <TeamFlag
            code={team1?.fifaCode ?? null}
            url={team1?.flagUrl ?? null}
            size="xs"
            mute
          />
          <span className="code">{team1?.fifaCode ?? '—'}</span>
          {lock1 !== null ? <LockBadge locked={lock1} /> : null}
          <span className="name">{team1?.name ?? t1}</span>
        </div>
        <div className={cn('mc-team', !team2 && 'unresolved', w2 && 'winner', w1 && 'loser')}>
          <span className="ind" />
          <TeamFlag
            code={team2?.fifaCode ?? null}
            url={team2?.flagUrl ?? null}
            size="xs"
            mute
          />
          <span className="code">{team2?.fifaCode ?? '—'}</span>
          {lock2 !== null ? <LockBadge locked={lock2} /> : null}
          <span className="name">{team2?.name ?? t2}</span>
        </div>
      </div>
      <ScoreBlock m={m} />
      <PredictionBlock m={m} prediction={prediction} />
    </Link>
  );
}
